import {
    timeSuggestionLabel,
    timeSuggestionAriaLabel,
    timeSuggestionLoading,
    timeSuggestionNotFound,
    timeSuggestionSchedulingAssistant,
    timeSuggestionSeparator as timeSuggestionSeparator_1,
    timeSuggestionTooltipRanked,
    timeSuggestionTooltipChronological,
    suggestedTimesLabel,
    timeSuggestionSettings,
    noSuggestedTimes,
    timeSuggestionsAdjustDurationAndTiming,
    timeSuggestionListItemAriaLabel,
    timeSuggestionTitleLabel,
    timeSuggestionTooltipNotAvailableYou,
    timeSuggestionTooltipAvailableEveryone,
    timeSuggestionTooltipUnavailableAttendee,
    timeSuggestionTooltipMultipleUnavailableAttendees,
} from './TimeSuggestions.locstring.json';
import loc, { format } from 'owa-localize';
import { getTimeSuggestionsStore } from '../selectors/getTimeSuggestionsStore';
import convertMeetingTimeSlotIntoOwaDates from '../utils/convertMeetingTimeSlotIntoOwaDates';
import { observer } from 'mobx-react-lite';
import { Link } from '@fluentui/react/lib/Link';
import { logUsage } from 'owa-analytics';
import { owaDate, OwaDate, formatWeekDayShortUserDate, isEqual, isSameDay } from 'owa-datetime';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { IconButton, ActionButton } from '@fluentui/react/lib/Button';
import React from 'react';
import { KeyCodes } from '@fluentui/utilities/lib';
import {
    logTimeSuggestionShown,
    logTimeSuggestionClickedIndex,
    logTimeSuggestionSAClickedCount,
    logTimeSuggestionStartLastSet,
    logTimeSuggestionInteraction,
    clearTimeSuggestionStartLastSet,
} from 'owa-calendar-compose-form-actions';
import type { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import { TimeSuggestionsRequestState } from '../store/schema/TimeSuggestionsRequestState';
import { formatSingleDayTimeSpanWithoutDate } from 'owa-timeformat/lib/utils/formatTimeSpan';
import type FindMeetingTimesResponse from 'owa-calendar-scheduling-service/lib/schema/FindMeetingTimesResponse';
import generateLinkStyle from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateLinkStyle';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { isFeatureEnabled } from 'owa-feature-flags';
import SearchCriteriaDialog from './SearchCriteriaDialog';
import { showSearchCriteriaDialog } from '../actions/internalActions';
import { findMeetingTimesConstants } from '../constants';
import { getCalendarThemedButtonStyle } from 'owa-component-styling-utils';
import type { TimeSuggestionsProps } from './TimeSuggestionsProps';
import { isAnyTimeSuggestionsExpEnabled } from '../utils/isAnyExperimentEnabled';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import getIsTimePanelView from '../selectors/getIsTimePanelView';
import { LiveNotification } from 'owa-live-notification';
import type EmailAddress from 'owa-service/lib/contract/EmailAddress';
import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';

import styles from './TimeSuggestions.scss';
import classnamesBind from 'classnames/bind';
let classNames = classnamesBind.bind(styles);

interface FormattedSuggestion {
    start: OwaDate;
    end: OwaDate;
    date: string;
    time: string;
    isCurrentlySelected: boolean;
    accommodationsCount?: number;
    unavailableAttendees?: EmailAddressWrapper[];
}

interface ExperimentProps extends TimeSuggestionsProps {
    suggestions: FormattedSuggestion[];
    timeSuggestionsRequestState: TimeSuggestionsRequestState;
    containerRectLeft: number;
    ariaRequestCompletionResourceId: string;
    onSearchCriteriaClicked: (ev?: any) => void;
    onTimeSuggestionSelected: (
        start: OwaDate,
        end: OwaDate,
        index: number,
        viewStateId: string | null
    ) => (ev) => void;
}

export default observer(function TimeSuggestions(props: TimeSuggestionsProps) {
    const container = React.useRef<HTMLDivElement>();
    const containerRectLeft = React.useRef<number>();
    const isExp = isAnyTimeSuggestionsExpEnabled();

    React.useLayoutEffect(() => {
        if (container.current && getIsTimePanelView(props.viewStateId)) {
            containerRectLeft.current = container.current.getBoundingClientRect().left;
        }
    }, []);

    const getTimeSuggestionContent = () => {
        const { colorScheme, hideSchedulingAssistantLink, viewStateId } = props;
        const { timeSuggestions, timeSuggestionsRequestState } = getTimeSuggestionsStore(
            viewStateId
        );

        switch (timeSuggestionsRequestState) {
            case TimeSuggestionsRequestState.SearchingSuggestions:
                return <div className={styles.noSuggestionText}>{loc(timeSuggestionLoading)}</div>;
            case TimeSuggestionsRequestState.NoSuggestionsFound:
                return (
                    <div className={styles.noSuggestionText}>
                        {loc(timeSuggestionNotFound)}
                        {!hideSchedulingAssistantLink && (
                            <Link
                                className={classNames(styles.link, styles.separator)}
                                styles={generateLinkStyle(colorScheme)}
                                onClick={onSchedulingAssistantClicked}>
                                {loc(timeSuggestionSchedulingAssistant)}
                            </Link>
                        )}
                    </div>
                );
            case TimeSuggestionsRequestState.ShowSuggestions:
                const suggestions = formatTimeSuggestions(timeSuggestions);
                logUsage('TimeSuggestionShown');
                logTimeSuggestionShown(suggestions.length, viewStateId);

                return suggestions.map((suggestion, index, array) => (
                    <div className={styles.suggestion} key={index}>
                        {getTimeSuggestionsPrefix(suggestion.date)}
                        <TooltipHost content={getSuggestionTooltip(index)}>
                            <Link
                                className={classNames(styles.link, {
                                    [styles.bold]: suggestion.isCurrentlySelected,
                                })}
                                styles={generateLinkStyle(colorScheme)}
                                onClick={onTimeSuggestionSelected(
                                    suggestion.start,
                                    suggestion.end,
                                    index,
                                    viewStateId
                                )}
                                role="option"
                                aria-label={getSuggestionAriaLabel(
                                    index,
                                    suggestion.start,
                                    suggestion.end
                                )}
                                aria-selected={suggestion.isCurrentlySelected}>
                                {suggestion.time}
                            </Link>
                        </TooltipHost>
                    </div>
                ));

            default:
                return null;
        }
    };

    const getSuggestionTooltip = (index: number): string => {
        if (index == 0 && isFeatureEnabled('cal-mf-sendrewardsfortimesuggestions')) {
            return loc(timeSuggestionTooltipRanked);
        }

        return loc(timeSuggestionTooltipChronological);
    };

    const getSuggestionAriaLabel = (index: number, start: OwaDate, end: OwaDate): string => {
        return format(
            loc(timeSuggestionListItemAriaLabel),
            formatMeetingTimeSpan(start, end),
            getSuggestionTooltip(index)
        );
    };

    /**
     * Gets the string to display before a time suggestion.
     * If this is the first time suggestion on a particular date, then return the date
     * Else, return the time suggestions separator (|)
     */
    const getTimeSuggestionsPrefix = (suggestionDate: string) => {
        if (suggestionDate) {
            return <span className={styles.dayLabel}>{suggestionDate}</span>;
        } else {
            return (
                <span className={styles.timeSuggestionSeparator}>
                    {loc(timeSuggestionSeparator_1)}
                </span>
            );
        }
    };

    /**
     * Handler for onTimeSuggestionSelected
     */
    const onTimeSuggestionSelected = (start, end, index, viewStateId) => ev => {
        if (ev.type === 'click' || (ev.type === 'keydown' && ev.keyCode === KeyCodes.enter)) {
            const { onTimeSuggestionClicked } = props;
            onTimeSuggestionClicked(start, end);
            logUsage('TimeSuggestionClicked');
            logTimeSuggestionClickedIndex(index, viewStateId);
        }
    };

    /**
     * Handler for onSchedulingAssistantClicked
     */
    const onSchedulingAssistantClicked = () => {
        const { onTimeSuggestionNotFound, viewStateId } = props;
        onTimeSuggestionNotFound();
        logTimeSuggestionSAClickedCount(viewStateId);
    };

    /**
     * Handler for onShowSearchCriteriaDialog
     */
    const onShowSearchCriteriaDialog = () => {
        logTimeSuggestionInteraction(props.viewStateId);
        logUsage('OpenTimeSuggestionSettings');
        showSearchCriteriaDialog(props.event, props.viewStateId);
    };

    const formatTimeSuggestions = (timeSuggestions: FindMeetingTimesResponse) => {
        const { event, maxCandidates, viewStateId } = props;
        const suggestions = timeSuggestions.MeetingTimeSuggestions.slice(0, maxCandidates);
        let previousSuggestionDate: OwaDate = null;

        clearTimeSuggestionStartLastSet(viewStateId);

        return suggestions.map(suggestion => {
            const owaDatesCurrent: OwaDateRange = convertMeetingTimeSlotIntoOwaDates(
                suggestion.MeetingTimeSlot,
                event,
                viewStateId
            );
            const owaDateCurrentStart = owaDate(event.Start, owaDatesCurrent.start);
            const owaDateCurrentEnd = owaDate(event.End, owaDatesCurrent.end);
            const startEndTime: string = formatSingleDayTimeSpanWithoutDate(
                owaDateCurrentStart,
                owaDateCurrentEnd
            );
            const isCurrentlySelected: boolean =
                isEqual(event.Start, owaDateCurrentStart) && isEqual(event.End, owaDateCurrentEnd);

            let startEndDate: string = isExp
                ? formatWeekDayShortUserDate(owaDateCurrentStart)
                : null;

            const accommodationsCount: number = suggestion.Accommodations?.length || 0;

            let unavailableAttendees: EmailAddressWrapper[] = [];

            const unavailableAttendeesAvailability = suggestion.AttendeeAvailability.filter(
                value => value.Availability == 'Busy'
            );

            unavailableAttendeesAvailability.forEach(item => {
                const email: string = (item.Attendee
                    .EmailAddress as EmailAddress).Address.toString();
                const name: string = event.OptionalAttendees.filter(
                    value => value.Mailbox.EmailAddress == email
                ).map(value => value.Mailbox.Name)[0];

                if (email && name) {
                    const attendee: EmailAddressWrapper = { Name: name, EmailAddress: email };
                    unavailableAttendees.push(attendee);
                }
            });

            logTimeSuggestionStartLastSet(owaDateCurrentStart, viewStateId);

            if (
                !previousSuggestionDate ||
                !isSameDay(previousSuggestionDate, owaDateCurrentStart)
            ) {
                previousSuggestionDate = owaDateCurrentStart;
                startEndDate = formatWeekDayShortUserDate(owaDateCurrentStart);
            }

            return {
                start: owaDateCurrentStart,
                end: owaDateCurrentEnd,
                date: startEndDate,
                time: startEndTime,
                isCurrentlySelected: isCurrentlySelected,
                accommodationsCount: accommodationsCount,
                unavailableAttendees: unavailableAttendees,
            };
        });
    };

    if (isExp) {
        const {
            timeSuggestions,
            timeSuggestionsRequestState,
            ariaRequestCompletionResourceId,
        } = getTimeSuggestionsStore(props.viewStateId);
        let suggestions;
        if (timeSuggestions?.MeetingTimeSuggestions?.length > 0) {
            suggestions = formatTimeSuggestions(timeSuggestions);
        }
        return (
            <TimeSuggestionExps
                {...props}
                ref={container}
                containerRectLeft={containerRectLeft.current}
                onSearchCriteriaClicked={onShowSearchCriteriaDialog}
                onTimeSuggestionSelected={onTimeSuggestionSelected}
                suggestions={suggestions}
                timeSuggestionsRequestState={timeSuggestionsRequestState}
                ariaRequestCompletionResourceId={ariaRequestCompletionResourceId}
            />
        );
    } else {
        const { ariaRequestCompletionResourceId } = getTimeSuggestionsStore(props.viewStateId);

        return (
            <div className={styles.suggestionsContainer}>
                <div className={styles.freeLabel}>{loc(timeSuggestionLabel)}</div>
                <div
                    className={styles.suggestions}
                    role="listbox"
                    aria-label={loc(timeSuggestionAriaLabel)}>
                    {getTimeSuggestionContent()}
                </div>
                <LiveNotification message={loc(ariaRequestCompletionResourceId)} atomic={true} />
            </div>
        );
    }
});

export const TimeSuggestionExps = React.forwardRef(function TimeSuggestionExps(
    props: ExperimentProps,
    ref: React.Ref<HTMLDivElement>
) {
    const {
        containerRectLeft,
        maxCandidates,
        colorScheme,
        event,
        ariaRequestCompletionResourceId,
        onSearchCriteriaClicked,
        viewStateId,
    } = props;

    const showCardLayout = isFeatureEnabled('mon-cal-showEditPaneInCards');

    return (
        <div ref={ref} className={classNames(styles.suggestionsContainerExp)}>
            <div
                className={classNames(styles.suggestionsExp)}
                role="listbox"
                aria-label={loc(timeSuggestionAriaLabel)}>
                {getTimeSuggestionContentExp(props)}
                <LiveNotification message={loc(ariaRequestCompletionResourceId)} atomic={true} />
            </div>
            <div className={classNames(styles.suggestionsHeader)}>
                {getIsTimePanelView(viewStateId)
                    ? loc(timeSuggestionTitleLabel)
                    : loc(suggestedTimesLabel)}
                {maxCandidates >= findMeetingTimesConstants.MAX_CANDIDATES ? (
                    <ActionButton
                        className={classNames(styles.suggestionsSettings, {
                            showCardLayout: showCardLayout,
                        })}
                        styles={getCalendarThemedButtonStyle(colorScheme)}
                        onClick={onSearchCriteriaClicked}>
                        {loc(timeSuggestionSettings)}
                    </ActionButton>
                ) : (
                    <IconButton
                        className={classNames(styles.suggestionsSettings, {
                            showCardLayout: showCardLayout,
                        })}
                        onClick={onSearchCriteriaClicked}
                        iconProps={{
                            iconName: ControlIcons.Equalizer,
                        }}
                        styles={getCalendarThemedButtonStyle(colorScheme)}
                    />
                )}
            </div>
            <SearchCriteriaDialog
                calendarEvent={event}
                containerRectLeft={containerRectLeft}
                viewStateId={viewStateId}
            />
        </div>
    );
});

function getTimeSuggestionContentExp(props: ExperimentProps) {
    const {
        colorScheme,
        onSearchCriteriaClicked,
        onTimeSuggestionSelected,
        suggestions,
        timeSuggestionsRequestState,
        viewStateId,
    } = props;

    const elementStyle: React.CSSProperties = {
        color: colorScheme.accessibleText.msColorWhiteBackground,
    };

    switch (timeSuggestionsRequestState) {
        case TimeSuggestionsRequestState.SearchingSuggestions:
            return (
                <div className={styles.noSuggestionText}>
                    <Spinner size={SpinnerSize.small} className={styles.inlineSpinner} />
                    {loc(timeSuggestionLoading)}
                </div>
            );
        case TimeSuggestionsRequestState.NoSuggestionsFound:
            return (
                <div
                    className={classNames(styles.noSuggestionContainer, styles.suggestionCont)}
                    onClick={onSearchCriteriaClicked}>
                    {loc(noSuggestedTimes)}
                    <div style={elementStyle}>{loc(timeSuggestionsAdjustDurationAndTiming)}</div>
                </div>
            );
        case TimeSuggestionsRequestState.ShowSuggestions:
            logUsage('TimeSuggestionShown');
            logTimeSuggestionShown(suggestions.length, viewStateId);

            return suggestions.map((suggestion, index, array) => (
                <div
                    className={classNames(styles.suggestionExp, styles.suggestionCont, {
                        [styles.selectedTimeSuggestion]: suggestion.isCurrentlySelected,
                    })}
                    key={index}
                    onClick={onTimeSuggestionSelected(
                        suggestion.start,
                        suggestion.end,
                        index,
                        viewStateId
                    )}
                    onKeyDown={onTimeSuggestionSelected(
                        suggestion.start,
                        suggestion.end,
                        index,
                        viewStateId
                    )}
                    role="option"
                    aria-selected={suggestion.isCurrentlySelected}
                    tabIndex={0}>
                    <span className={styles.dayLabel}>{suggestion.date}</span>
                    <span className={styles.timeSuggestion}>{suggestion.time}</span>
                    {suggestion.accommodationsCount == 0 && renderSuggestionContext(suggestion)}
                    {suggestion.accommodationsCount > 0 && (
                        <div>
                            <span className={styles.suggestionContext}>
                                <Icon
                                    iconName={ControlIcons.AlertSolid}
                                    className={classNames(styles.suggestionContextIcon, {
                                        grey: true,
                                    })}
                                />
                                {loc(timeSuggestionTooltipNotAvailableYou)}
                            </span>
                        </div>
                    )}
                </div>
            ));

        default:
            return null;
    }
}

function renderSuggestionContext(suggestion) {
    if (
        isFeatureEnabled('cal-mf-timeAccommodations') &&
        isFeatureEnabled('cal-mf-filterBusyOptionalAttendees') &&
        suggestion.unavailableAttendees?.length >= 1
    ) {
        return (
            <TooltipHost
                className={styles.unavailableAttendeesTooltip}
                content={suggestion.unavailableAttendees.map(a => a.Name).join('\r\n')}
                directionalHint={DirectionalHint.bottomCenter}>
                <span className={styles.suggestionContext}>
                    <Icon
                        iconName={ControlIcons.AlertSolid}
                        className={classNames(styles.suggestionContextIcon, {
                            grey: true,
                        })}
                    />
                    {suggestion.unavailableAttendees?.length > 1
                        ? loc(
                              timeSuggestionTooltipMultipleUnavailableAttendees,
                              suggestion.unavailableAttendees.length
                          )
                        : loc(timeSuggestionTooltipUnavailableAttendee)}
                </span>
            </TooltipHost>
        );
    } else {
        return (
            <span className={styles.suggestionContext}>
                <Icon
                    iconName={ControlIcons.CompletedSolid}
                    className={styles.suggestionContextIcon}
                />
                {loc(timeSuggestionTooltipAvailableEveryone)}
            </span>
        );
    }
}
